import React, { useEffect, useState, useRef } from 'react';
import { UserIcon, ShieldIcon, BellIcon, CameraIcon, SaveIcon, EyeIcon, EyeOffIcon, CheckIcon, MapPinIcon, DollarSignIcon, ShoppingCartIcon, FileTextIcon, UsersIcon, CalendarIcon, LockIcon, KeyIcon, AlertTriangleIcon, GlobeIcon, Loader2Icon, SettingsIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Card } from '../components/ui/Card';
import { PageHeader } from '../components/ui/PageHeader';
import { useAuth } from '../context/AuthContext';
import { supabaseAdmin } from '../lib/supabase';
import { getGoogleApiKey, setGoogleApiKey, validateGoogleApiKey, hasGoogleApiKey } from '../lib/googleMaps';
type Tab = 'perfil' | 'seguridad' | 'notificaciones' | 'integraciones';
const AVATAR_KEY = 'ventastrack_avatar';
const NOTIFS_KEY = 'ventastrack_notifs';
const defaultNotifs = {
  visitasCompletadas: true,
  nuevosCobros: true,
  nuevasVentas: false,
  reportesDiarios: true,
  alertasEquipo: false,
  recordatoriosVisita: true
};
export type NotifPrefs = typeof defaultNotifs;
export function loadNotifPrefs(): NotifPrefs {
  try {
    const saved = localStorage.getItem(NOTIFS_KEY);
    if (saved) return {
      ...defaultNotifs,
      ...JSON.parse(saved)
    };
  } catch {}
  return defaultNotifs;
}
export function loadAvatarUrl(userId?: string): string {
  if (userId) {
    const userSpecific = localStorage.getItem(`ventastrack_avatar_${userId}`);
    if (userSpecific) return userSpecific;
  }
  // Don't fall back to global key — each user has their own avatar
  return '';
}
export function SettingsPage() {
  const {
    user,
    addActivityLog
  } = useAuth();
  const isAdmin = user?.rolNombre === 'Administrador' || user?.permisos?.users === true;
  // Permission helpers for settings sub-tabs
  const canSeePerfil = isAdmin || user?.permisos?.settings_perfil === true || user?.permisos?.settings_perfil === undefined;
  const canSeeSeguridad = isAdmin || user?.permisos?.settings_seguridad === true || user?.permisos?.settings_seguridad === undefined;
  const canSeeNotificaciones = isAdmin || user?.permisos?.settings_notificaciones === true || user?.permisos?.settings_notificaciones === undefined;
  const canSeeIntegraciones = isAdmin || user?.permisos?.settings_integraciones === true;
  const [activeTab, setActiveTab] = useState<Tab>('perfil');
  // Perfil
  const [avatarUrl, setAvatarUrl] = useState<string>(() => loadAvatarUrl(user?.id));
  const [nombre, setNombre] = useState(user?.nombre || '');
  // Cargar avatar desde Supabase al iniciar (para sincronizar entre navegadores)
  useEffect(() => {
    if (!user?.id) return;
    const loadFromSupabase = async () => {
      const {
        data
      } = await supabaseAdmin.from('profiles').select('avatar_url').eq('id', user.id).single();
      if (data?.avatar_url) {
        setAvatarUrl(data.avatar_url);
        localStorage.setItem(`ventastrack_avatar_${user.id}`, data.avatar_url);
        window.dispatchEvent(new Event('avatar-updated'));
      }
    };
    loadFromSupabase();
  }, [user?.id]);
  const [email, setEmail] = useState(user?.email || '');
  const [perfilSaved, setPerfilSaved] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Seguridad
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSaved, setPasswordSaved] = useState(false);
  // Notificaciones
  const [notifs, setNotifs] = useState<NotifPrefs>(loadNotifPrefs);
  const [notifSaved, setNotifSaved] = useState(false);
  // Integraciones
  const [googleKey, setGoogleKey] = useState(getGoogleApiKey);
  const [googleKeyInput, setGoogleKeyInput] = useState(getGoogleApiKey);
  const [isValidatingKey, setIsValidatingKey] = useState(false);
  const [keyValidation, setKeyValidation] = useState<'idle' | 'valid' | 'invalid'>('idle');
  const [integrationSaved, setIntegrationSaved] = useState(false);
  const getInitials = (name: string) => name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
  const getPasswordStrength = (pwd: string) => {
    if (!pwd) return 0;
    let score = 0;
    if (pwd.length >= 6) score++;
    if (pwd.length >= 10) score++;
    if (/[A-Z]/.test(pwd)) score++;
    if (/[0-9]/.test(pwd) || /[^A-Za-z0-9]/.test(pwd)) score++;
    return score;
  };
  const strengthColors = ['bg-rose-500', 'bg-orange-500', 'bg-amber-500', 'bg-emerald-500'];
  const strengthLabels = ['Muy débil', 'Débil', 'Buena', 'Fuerte'];
  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async ev => {
      const base64 = ev.target?.result as string;
      setAvatarUrl(base64);
      if (user?.id) {
        localStorage.setItem(`ventastrack_avatar_${user.id}`, base64);
        // Guardar en Supabase para sincronizar entre navegadores
        await supabaseAdmin.from('profiles').update({
          avatar_url: base64
        }).eq('id', user.id);
      }
      window.dispatchEvent(new Event('avatar-updated'));
    };
    reader.readAsDataURL(file);
  };
  const handleSavePerfil = () => {
    setPerfilSaved(true);
    setTimeout(() => setPerfilSaved(false), 2500);
  };
  const handleSavePassword = () => {
    setPasswordError('');
    if (!currentPassword) {
      setPasswordError('Ingresa tu contraseña actual');
      return;
    }
    if (newPassword.length < 6) {
      setPasswordError('La nueva contraseña debe tener al menos 6 caracteres');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Las contraseñas no coinciden');
      return;
    }
    addActivityLog('Cambió contraseña', 'Actualización de contraseña');
    setPasswordSaved(true);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setTimeout(() => setPasswordSaved(false), 2500);
  };
  const handleToggleNotif = (key: keyof NotifPrefs) => {
    setNotifs(prev => {
      const updated = {
        ...prev,
        [key]: !prev[key]
      };
      localStorage.setItem(NOTIFS_KEY, JSON.stringify(updated));
      window.dispatchEvent(new Event('notifs-updated'));
      return updated;
    });
  };
  const handleSaveNotifs = () => {
    localStorage.setItem(NOTIFS_KEY, JSON.stringify(notifs));
    window.dispatchEvent(new Event('notifs-updated'));
    setNotifSaved(true);
    setTimeout(() => setNotifSaved(false), 2500);
  };
  const handleValidateGoogleKey = async () => {
    if (!googleKeyInput.trim()) return;
    setIsValidatingKey(true);
    setKeyValidation('idle');
    try {
      const isValid = await validateGoogleApiKey(googleKeyInput.trim());
      setKeyValidation(isValid ? 'valid' : 'invalid');
    } catch {
      setKeyValidation('invalid');
    } finally {
      setIsValidatingKey(false);
    }
  };
  const handleSaveGoogleKey = () => {
    setGoogleApiKey(googleKeyInput.trim());
    setGoogleKey(googleKeyInput.trim());
    addActivityLog('Guardó API Key Google Maps', 'Integración Google Maps configurada');
    setIntegrationSaved(true);
    setTimeout(() => setIntegrationSaved(false), 2500);
  };
  const handleRemoveGoogleKey = () => {
    setGoogleApiKey('');
    setGoogleKey('');
    setGoogleKeyInput('');
    setKeyValidation('idle');
    addActivityLog('Eliminó API Key Google Maps', 'Integración Google Maps desconectada');
    setIntegrationSaved(true);
    setTimeout(() => setIntegrationSaved(false), 2500);
  };
  const notifItems: {
    key: keyof NotifPrefs;
    label: string;
    desc: string;
    icon: React.ReactNode;
    color: string;
  }[] = [{
    key: 'visitasCompletadas',
    label: 'Visitas completadas',
    desc: 'Cuando un vendedor completa una visita',
    icon: <MapPinIcon className="w-4 h-4" data-id="element-4493" data-mp="37mkk2vy47ari" />,
    color: 'bg-[#eef2fc] text-[#2952C4]'
  }, {
    key: 'nuevosCobros',
    label: 'Nuevos cobros',
    desc: 'Alertas de cobros registrados en el sistema',
    icon: <DollarSignIcon className="w-4 h-4" data-id="element-4494" data-mp="3hcnbwsti2io9" />,
    color: 'bg-emerald-100 text-emerald-600'
  }, {
    key: 'nuevasVentas',
    label: 'Nuevas ventas',
    desc: 'Alertas de ventas registradas',
    icon: <ShoppingCartIcon className="w-4 h-4" data-id="element-4495" data-mp="2onq27e3ctu74" />,
    color: 'bg-blue-100 text-blue-600'
  }, {
    key: 'reportesDiarios',
    label: 'Reportes diarios',
    desc: 'Resumen diario de actividad del equipo',
    icon: <FileTextIcon className="w-4 h-4" data-id="element-4496" data-mp="2yk30yqdray8q" />,
    color: 'bg-[#d5e0f7] text-[#1E3D96]'
  }, {
    key: 'alertasEquipo',
    label: 'Alertas del equipo',
    desc: 'Actividad y cambios del equipo comercial',
    icon: <UsersIcon className="w-4 h-4" data-id="element-4497" data-mp="du3t0gwpop64" />,
    color: 'bg-amber-100 text-amber-600'
  }, {
    key: 'recordatoriosVisita',
    label: 'Recordatorios de visita',
    desc: 'Recordatorios de próximas visitas programadas',
    icon: <CalendarIcon className="w-4 h-4" data-id="element-4498" data-mp="qeslhwnu4kax" />,
    color: 'bg-rose-100 text-rose-600'
  }];
  const allTabs: {
    id: Tab;
    label: string;
    icon: React.ReactNode;
    visible: boolean;
  }[] = [{
    id: 'perfil',
    label: 'Perfil',
    icon: <UserIcon className="w-5 h-5" data-id="element-4499" data-mp="144dzstqowbcm" />,
    visible: canSeePerfil
  }, {
    id: 'seguridad',
    label: 'Seguridad',
    icon: <ShieldIcon className="w-5 h-5" data-id="element-4500" data-mp="192xo30sr9z85" />,
    visible: canSeeSeguridad
  }, {
    id: 'notificaciones',
    label: 'Notificaciones',
    icon: <BellIcon className="w-5 h-5" data-id="element-4501" data-mp="vahroiud0zg7" />,
    visible: canSeeNotificaciones
  }, {
    id: 'integraciones',
    label: 'Integraciones',
    icon: <GlobeIcon className="w-5 h-5" data-id="element-4502" data-mp="1n407t6wa2xfw" />,
    visible: canSeeIntegraciones
  }];
  const tabs = allTabs.filter(t => t.visible);
  // Auto-select first visible tab if current is hidden
  useEffect(() => {
    if (tabs.length > 0 && !tabs.find(t => t.id === activeTab)) {
      setActiveTab(tabs[0].id);
    }
  }, [tabs, activeTab]);
  const activeNotifCount = Object.values(notifs).filter(Boolean).length;
  const pwdStrength = getPasswordStrength(newPassword);
  return <div className="max-w-4xl mx-auto space-y-6" data-id="element-4503" data-mp="317utw2zdbd5h">
      <PageHeader title="Configuración" subtitle="Ajustes de cuenta y preferencias del sistema" icon={<SettingsIcon className="w-6 h-6" data-id="element-4505" data-mp="2sd6nualyxrfm" />} data-id="element-4504" data-mp="3641ztvrrkonf" />
      

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 animate-stagger-1" data-id="element-4506" data-mp="2sbdget3j736k">
        {/* Sidebar */}
        <div className="md:col-span-1 space-y-4" data-id="element-4507" data-mp="2x5ztr1t6b5ta">
          {/* User card */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 text-center" data-id="element-4508" data-mp="2ih3ofcii46wd">
            <div className="relative inline-block mb-4" data-id="element-4509" data-mp="2nen1wa7dkfn9">
              <div className="w-20 h-20 rounded-2xl overflow-hidden flex items-center justify-center shadow-lg mx-auto" style={{
              background: 'linear-gradient(135deg, #3B65D7, #2952C4)'
            }} data-id="element-4510" data-mp="29ec7wyarqkxv">
                
                {avatarUrl ? <img src={avatarUrl} alt="avatar" className="w-full h-full object-cover" data-id="element-4511" data-mp="1us5dmil5pwbj" /> : <span className="text-2xl font-bold text-white" data-id="element-4512" data-mp="1pu3vbzvk2114">
                    {getInitials(nombre || user?.nombre || '??')}
                  </span>}
              </div>
              <button onClick={() => fileInputRef.current?.click()} className="absolute -bottom-1.5 -right-1.5 w-8 h-8 rounded-xl flex items-center justify-center shadow-lg transition-colors" style={{
              backgroundColor: '#2952C4'
            }} onMouseEnter={e => e.currentTarget.style.backgroundColor = '#1E3D96'} onMouseLeave={e => e.currentTarget.style.backgroundColor = '#2952C4'} data-id="element-4513" data-mp="1yt7gd9ea8qi9">
                
                <CameraIcon className="w-3.5 h-3.5 text-white" data-id="element-4514" data-mp="2iirbxi5d028c" />
              </button>
              <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleAvatarChange} data-id="element-4515" data-mp="2wgfuukfv8zar" />
              
            </div>
            <p className="font-bold text-slate-900 font-heading" data-id="element-4516" data-mp="2rgbecueyk4kj">
              {user?.nombre}
            </p>
            <p className="text-sm text-slate-500 mt-0.5" data-id="element-4517" data-mp="1t6bkv6q8supr">{user?.rolNombre}</p>
            <span className="inline-flex items-center gap-1.5 mt-3 px-3 py-1 bg-emerald-50 text-emerald-700 text-xs font-semibold rounded-full border border-emerald-200" data-id="element-4518" data-mp="1t48w0q0tt7mm">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" data-id="element-4519" data-mp="22wwqhe4c2zi6" />
              Activo
            </span>
          </div>

          {/* Tab navigation */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-id="element-4520" data-mp="2cu2l2wc5p8n5">
            {tabs.map((tab, idx) => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`w-full flex items-center gap-3 px-5 py-4 text-sm font-medium transition-all ${idx < tabs.length - 1 ? 'border-b border-slate-100' : ''}`} style={activeTab === tab.id ? {
            backgroundColor: '#eef2fc',
            color: '#1E3D96',
            borderRight: '2px solid #2952C4'
          } : {}} onMouseEnter={e => {
            if (activeTab !== tab.id) e.currentTarget.style.backgroundColor = '#f8fafc';
          }} onMouseLeave={e => {
            if (activeTab !== tab.id) e.currentTarget.style.backgroundColor = '';
          }} data-id="element-4521" data-mp="2gwz76x0wnmvu" data-mpi={idx}>
              
                <span style={activeTab === tab.id ? {
              color: '#2952C4'
            } : {
              color: '#94a3b8'
            }} data-id="element-4522" data-mp="a5kmxl8q82yr">
                
                  {tab.icon}
                </span>
                <span className={activeTab === tab.id ? 'font-heading' : 'text-slate-600 font-heading'} data-id="element-4523" data-mp="xx877kbhgfs2">
                
                  {tab.label}
                </span>
                {tab.id === 'notificaciones' && <span className="ml-auto text-xs font-bold px-2 py-0.5 rounded-full" style={{
              backgroundColor: '#eef2fc',
              color: '#2952C4'
            }} data-id="element-4524" data-mp="owtkqhms8umz">
                
                    {activeNotifCount}
                  </span>}
              </button>)}
          </div>
        </div>

        {/* Right content */}
        <div className="md:col-span-3" data-id="element-4525" data-mp="11chxtgeivne9">
          {/* PERFIL TAB */}
          {activeTab === 'perfil' && canSeePerfil && <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-id="element-4526" data-mp="16640qguvwcwv">
              <div className="px-8 py-6 border-b border-slate-100" style={{
            background: 'linear-gradient(90deg, #eef2fc, #f8fafc)'
          }} data-id="element-4527" data-mp="rdtqpco2sspc">
              
                <h3 className="text-lg font-bold text-slate-900 font-heading" data-id="element-4528" data-mp="1k5vjeuwcihh7">
                  Información del Perfil
                </h3>
                <p className="text-sm text-slate-500 mt-0.5" data-id="element-4529" data-mp="1p1avpiepg5bi">
                  Actualiza tu información personal y foto de perfil
                </p>
              </div>

              <div className="p-8 space-y-6" data-id="element-4530" data-mp="1f3wj5tvwk0oh">
                <div className="flex items-center gap-6 p-5 bg-slate-50 rounded-2xl border border-dashed border-slate-200" data-id="element-4531" data-mp="3vuczypuj92ft">
                  <div className="relative flex-shrink-0" data-id="element-4532" data-mp="3rdhs7s2jlen">
                    <div className="w-20 h-20 rounded-2xl overflow-hidden flex items-center justify-center shadow-md" style={{
                  background: 'linear-gradient(135deg, #3B65D7, #2952C4)'
                }} data-id="element-4533" data-mp="3sc76tk7zl1si">
                    
                      {avatarUrl ? <img src={avatarUrl} alt="avatar" className="w-full h-full object-cover" data-id="element-4534" data-mp="64h0x93dfuir" /> : <span className="text-2xl font-bold text-white" data-id="element-4535" data-mp="15mhc4xky1xl">
                          {getInitials(nombre || user?.nombre || '??')}
                        </span>}
                    </div>
                  </div>
                  <div className="flex-1" data-id="element-4536" data-mp="34o62knhuc7iw">
                    <p className="text-sm font-semibold text-slate-900 mb-1" data-id="element-4537" data-mp="3jbis0narqxek">
                      Foto de perfil
                    </p>
                    <p className="text-xs text-slate-500 mb-3" data-id="element-4538" data-mp="3ejr98yxutvyz">
                      JPG, PNG o GIF. Máximo 5MB. Se guarda automáticamente.
                    </p>
                    <div className="flex items-center gap-2" data-id="element-4539" data-mp="2lwd8squy6ia4">
                      <button onClick={() => fileInputRef.current?.click()} className="flex items-center gap-2 px-4 py-2 text-white text-sm font-medium rounded-xl transition-colors shadow-sm" style={{
                    backgroundColor: '#2952C4'
                  }} onMouseEnter={e => e.currentTarget.style.backgroundColor = '#1E3D96'} onMouseLeave={e => e.currentTarget.style.backgroundColor = '#2952C4'} data-id="element-4540" data-mp="2lvv6pdllt9ua">
                      
                        <CameraIcon className="w-4 h-4" data-id="element-4541" data-mp="2slo96e9pmw77" />
                        Subir foto
                      </button>
                      {avatarUrl && <button onClick={async () => {
                    setAvatarUrl('');
                    if (user?.id) {
                      localStorage.removeItem(`ventastrack_avatar_${user.id}`);
                      await supabaseAdmin.from('profiles').update({
                        avatar_url: null
                      }).eq('id', user.id);
                    }
                    window.dispatchEvent(new Event('avatar-updated'));
                  }} className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-rose-600 hover:bg-rose-50 rounded-xl transition-colors" data-id="element-4542" data-mp="2wnrndanrkew6">
                      
                          Eliminar
                        </button>}
                    </div>
                  </div>
                </div>

                {isAdmin ? <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-4543" data-mp="1romor5by6xns">
                    <Input label="Nombre Completo" value={nombre} onChange={e => setNombre(e.target.value)} placeholder="Tu nombre completo" data-id="element-4544" data-mp="1rm8tavdcvjfp" />
                
                    <Input label="Usuario o Correo" type="text" autoComplete="off" value={email} onChange={e => setEmail(e.target.value)} placeholder="tu-usuario  o  tu@correo.com" data-id="element-4545" data-mp="1imlpy412g3k0" />
                
                  </div> : <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-4546" data-mp="18qs948gbdnwf">
                    <div data-id="element-4547" data-mp="381a4aat7vvko">
                      <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-4548" data-mp="381jxe79k6iul">
                        Nombre Completo
                      </label>
                      <div className="flex items-center gap-3 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl" data-id="element-4549" data-mp="2bxmgd93mnbub">
                        <UserIcon className="w-4 h-4 text-slate-400" data-id="element-4550" data-mp="2gvo1dzkm5l8f" />
                        <span className="text-sm text-slate-600" data-id="element-4551" data-mp="23643p85w3e5z">
                          {user?.nombre}
                        </span>
                        <span className="ml-auto text-xs text-slate-400" data-id="element-4552" data-mp="275mnu1pvzl7r">
                          Solo admin
                        </span>
                      </div>
                    </div>
                    <div data-id="element-4553" data-mp="1y82jul0n3atg">
                      <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-4554" data-mp="1y4ervqauewgu">
                        Correo Electrónico
                      </label>
                      <div className="flex items-center gap-3 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl" data-id="element-4555" data-mp="1o938guohp12r">
                        <span className="text-sm text-slate-600" data-id="element-4556" data-mp="3elivzyn5lhur">
                          {user?.email || '—'}
                        </span>
                        <span className="ml-auto text-xs text-slate-400" data-id="element-4557" data-mp="16yhix73js0ni">
                          Solo admin
                        </span>
                      </div>
                    </div>
                  </div>}

                <div data-id="element-4558" data-mp="1gp2iawr1p97h">
                  <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-4559" data-mp="1gqdikmla7ytu">
                    Rol del Sistema
                  </label>
                  <div className="flex items-center gap-3 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl" data-id="element-4560" data-mp="1qmq9jcpfzykw">
                    <ShieldIcon className="w-4 h-4 text-slate-400" data-id="element-4561" data-mp="1loormv3r65b0" />
                    <span className="text-sm text-slate-600" data-id="element-4562" data-mp="1znoga2r9mk76">
                      {user?.rolNombre}
                    </span>
                    <span className="ml-auto text-xs text-slate-400" data-id="element-4563" data-mp="1uobullc4rj66">
                      No editable
                    </span>
                  </div>
                </div>
              </div>

              <div className="px-8 py-5 bg-slate-50 border-t border-slate-100 flex items-center justify-end gap-3" data-id="element-4564" data-mp="b2fne0tf5gj">
                {perfilSaved && <div className="flex items-center gap-2 text-emerald-600 text-sm font-semibold" data-id="element-4565" data-mp="34pwrttr6szby">
                    <CheckIcon className="w-4 h-4" data-id="element-4566" data-mp="2zrv959xob210" />
                    Cambios guardados
                  </div>}
                <Button onClick={handleSavePerfil} leftIcon={<SaveIcon className="w-4 h-4" data-id="element-4568" data-mp="39ho91dogdspf" />} data-id="element-4567" data-mp="3dizzflf5ihyo">
                
                  Guardar Cambios
                </Button>
              </div>
            </div>}

          {/* SEGURIDAD TAB */}
          {activeTab === 'seguridad' && canSeeSeguridad && <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-id="element-4569" data-mp="2gv3iugjprmng">
              <div className="px-8 py-6 border-b border-slate-100 bg-slate-50" data-id="element-4570" data-mp="2uuuzyq5j1j5o">
                <h3 className="text-lg font-bold text-slate-900 font-heading" data-id="element-4571" data-mp="2ptfh94qtnoqg">
                  Seguridad de la Cuenta
                </h3>
                <p className="text-sm text-slate-500 mt-0.5" data-id="element-4572" data-mp="a4vxuxb5y4az">
                  Actualiza tu contraseña para mantener tu cuenta segura
                </p>
              </div>

              <div className="p-8 space-y-6" data-id="element-4573" data-mp="djfevbo7z6jv">
                <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl" data-id="element-4574" data-mp="hmc25fopfqu5">
                  <AlertTriangleIcon className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-4575" data-mp="3nc8tuq8ynrn" />
                  <div data-id="element-4576" data-mp="8ldrwfnoan34">
                    <p className="text-sm font-semibold text-amber-800" data-id="element-4577" data-mp="8myqqhh0ss6s">
                      Recomendación de seguridad
                    </p>
                    <p className="text-xs text-amber-700 mt-0.5" data-id="element-4578" data-mp="wdudv1aj6z7p">
                      Usa una contraseña de al menos 10 caracteres con letras,
                      números y símbolos.
                    </p>
                  </div>
                </div>

                <div className="space-y-5" data-id="element-4579" data-mp="rjgnxeen1kaf">
                  <div data-id="element-4580" data-mp="rif3ijbqflg1">
                    <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-4581" data-mp="1f9u5gazlk0qr">
                      Contraseña Actual
                    </label>
                    <div className="relative" data-id="element-4582" data-mp="1k35fsm3maznf">
                      <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" data-id="element-4583" data-mp="1txb1bpv9sfoh">
                        <KeyIcon className="w-4 h-4" data-id="element-4584" data-mp="1oxonbe7mavem" />
                      </div>
                      <input type={showCurrent ? 'text' : 'password'} value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} placeholder="••••••••" className="w-full pl-10 pr-12 py-3 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 transition-all bg-slate-50 focus:bg-white" style={{
                    '--tw-ring-color': 'rgba(41,82,196,0.2)',
                    '--tw-border-opacity': '1'
                  } as React.CSSProperties} data-id="element-4585" data-mp="1jylinuvjur76" />
                    
                      <button type="button" onClick={() => setShowCurrent(!showCurrent)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-id="element-4586" data-mp="w9su4jh9blml">
                      
                        {showCurrent ? <EyeOffIcon className="w-4 h-4" data-id="element-4587" data-mp="n9nl64wf0f6b" /> : <EyeIcon className="w-4 h-4" data-id="element-4588" data-mp="nap9vc6ct06i" />}
                      </button>
                    </div>
                  </div>

                  <div data-id="element-4589" data-mp="33usna2q7tba4">
                    <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-4590" data-mp="33tzp3q5bnu3l">
                      Nueva Contraseña
                    </label>
                    <div className="relative" data-id="element-4591" data-mp="37ut948fn8or7">
                      <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" data-id="element-4592" data-mp="3cuo3ilymnwh0">
                        <LockIcon className="w-4 h-4" data-id="element-4593" data-mp="2g6hrhdzdm1sp" />
                      </div>
                      <input type={showNew ? 'text' : 'password'} value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="••••••••" className="w-full pl-10 pr-12 py-3 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 transition-all bg-slate-50 focus:bg-white" data-id="element-4594" data-mp="2k6a4c2vsyu3v" />
                    
                      <button type="button" onClick={() => setShowNew(!showNew)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-id="element-4595" data-mp="1wgxvx58auq2m">
                      
                        {showNew ? <EyeOffIcon className="w-4 h-4" data-id="element-4596" data-mp="7y34pe0p0c00" /> : <EyeIcon className="w-4 h-4" data-id="element-4597" data-mp="2z0bqrxzlw8e" />}
                      </button>
                    </div>
                    {newPassword && <div className="mt-3" data-id="element-4598" data-mp="gqez1ayxgjl9">
                        <div className="flex gap-1.5 mb-1.5" data-id="element-4599" data-mp="gs9islygq5nr">
                          {[0, 1, 2, 3].map(i => <div key={i} className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${i < pwdStrength ? strengthColors[pwdStrength - 1] : 'bg-slate-200'}`} data-id="element-4600" data-mp="by58pttwhhig" />)}
                        </div>
                        <p className={`text-xs font-medium ${pwdStrength <= 1 ? 'text-rose-500' : pwdStrength === 2 ? 'text-amber-500' : pwdStrength === 3 ? 'text-amber-600' : 'text-emerald-600'}`} data-id="element-4601" data-mp="lrcal4pfmfds">
                      
                          {strengthLabels[pwdStrength - 1] || 'Muy débil'}
                        </p>
                      </div>}
                  </div>

                  <div data-id="element-4602" data-mp="2xgdb0lwtxyt2">
                    <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-4603" data-mp="2xgvikeoxzzga">
                      Confirmar Nueva Contraseña
                    </label>
                    <div className="relative" data-id="element-4604" data-mp="2rb5q30q4mlme">
                      <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" data-id="element-4605" data-mp="2dl29oqdxhm4v">
                        <LockIcon className="w-4 h-4" data-id="element-4606" data-mp="2ik5fid1qfwdw" />
                      </div>
                      <input type={showConfirm ? 'text' : 'password'} value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="••••••••" className={`w-full pl-10 pr-12 py-3 text-sm border rounded-xl focus:outline-none focus:ring-2 transition-all bg-slate-50 focus:bg-white ${confirmPassword && newPassword ? confirmPassword === newPassword ? 'border-emerald-400' : 'border-rose-400' : 'border-slate-200'}`} data-id="element-4607" data-mp="2ii0zwxuh18xs" />
                    
                      <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-id="element-4608" data-mp="3p193177k6frb">
                      
                        {showConfirm ? <EyeOffIcon className="w-4 h-4" data-id="element-4609" data-mp="3rm6yadiqlhsc" /> : <EyeIcon className="w-4 h-4" data-id="element-4610" data-mp="3nj20updik6ak" />}
                      </button>
                    </div>
                    {confirmPassword && newPassword && <p className={`mt-1.5 text-xs font-medium flex items-center gap-1 ${confirmPassword === newPassword ? 'text-emerald-600' : 'text-rose-500'}`} data-id="element-4611" data-mp="3ikqpubbhfpqr">
                    
                        {confirmPassword === newPassword ? <>
                            <CheckIcon className="w-3 h-3" data-id="element-4612" data-mp="a45n3c9g1sw3" />
                            Las contraseñas coinciden
                          </> : '✗ Las contraseñas no coinciden'}
                      </p>}
                  </div>
                </div>

                {passwordError && <div className="flex items-center gap-3 p-4 bg-rose-50 border border-rose-200 rounded-xl text-sm text-rose-700" data-id="element-4613" data-mp="e2n2s3wneo9">
                    <AlertTriangleIcon className="w-4 h-4 flex-shrink-0" data-id="element-4614" data-mp="t306iyjurhil" />
                    {passwordError}
                  </div>}
              </div>

              <div className="px-8 py-5 bg-slate-50 border-t border-slate-100 flex items-center justify-end gap-3" data-id="element-4615" data-mp="10fg2crt56i42">
                {passwordSaved && <div className="flex items-center gap-2 text-emerald-600 text-sm font-semibold" data-id="element-4616" data-mp="15f2hx7wg0ag1">
                    <CheckIcon className="w-4 h-4" data-id="element-4617" data-mp="1aeovjig6nspd" />
                    Contraseña actualizada
                  </div>}
                <Button onClick={handleSavePassword} leftIcon={<ShieldIcon className="w-4 h-4" data-id="element-4619" data-mp="1jchc0pxn56ec" />} data-id="element-4618" data-mp="1jda90z6sshja">
                
                  Actualizar Contraseña
                </Button>
              </div>
            </div>}

          {/* NOTIFICACIONES TAB */}
          {activeTab === 'notificaciones' && canSeeNotificaciones && <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-id="element-4620" data-mp="1y26gau115qiu">
              <div className="px-8 py-6 border-b border-slate-100" style={{
            background: 'linear-gradient(90deg, #eef2fc, #f8fafc)'
          }} data-id="element-4621" data-mp="231stx4krt8s6">
              
                <div className="flex items-center justify-between" data-id="element-4622" data-mp="2sdjlzu080446">
                  <div data-id="element-4623" data-mp="2sbqhak963tyt">
                    <h3 className="text-lg font-bold text-slate-900 font-heading" data-id="element-4624" data-mp="2obyg4sm3sq3h">
                      Notificaciones
                    </h3>
                    <p className="text-sm text-slate-500 mt-0.5" data-id="element-4625" data-mp="32ao8juxza1li">
                      Controla qué alertas deseas recibir
                    </p>
                  </div>
                  <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-xl border border-slate-200 shadow-sm" data-id="element-4626" data-mp="29miq5euecoou">
                    <BellIcon className="w-4 h-4" style={{
                  color: '#2952C4'
                }} data-id="element-4627" data-mp="24p0ehgozvvd5" />
                  
                    <span className="text-sm font-bold text-slate-900" data-id="element-4628" data-mp="2jey7qb1s7b97">
                      {activeNotifCount}
                    </span>
                    <span className="text-xs text-slate-500" data-id="element-4629" data-mp="1qw0zdft4k364">activas</span>
                  </div>
                </div>
              </div>

              <div className="p-6 space-y-3" data-id="element-4630" data-mp="1lwy60smibldz">
                {notifItems.map(item => <div key={item.key} className="flex items-center justify-between p-4 rounded-xl border transition-all" style={notifs[item.key] ? {
              borderColor: '#aac1ef',
              backgroundColor: 'rgba(238,242,252,0.5)'
            } : {
              borderColor: '#f1f5f9',
              backgroundColor: 'white'
            }} data-id="element-4631" data-mp="1f6spide0t9y">
                
                    <div className="flex items-center gap-4" data-id="element-4632" data-mp="k5wwd9as8k3v">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${item.color}`} data-id="element-4633" data-mp="k6pw3qbbq9ci">
                    
                        {item.icon}
                      </div>
                      <div data-id="element-4634" data-mp="b7clgubgreq7">
                        <p className="text-sm font-semibold text-slate-900" data-id="element-4635" data-mp="g430debj84a1">
                          {item.label}
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5" data-id="element-4636" data-mp="2vuo7z68vg2f1">
                          {item.desc}
                        </p>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer ml-4 flex-shrink-0" data-id="element-4637" data-mp="1yw90eitbaw8v">
                      <input type="checkbox" className="sr-only peer" checked={notifs[item.key]} onChange={() => handleToggleNotif(item.key)} data-id="element-4638" data-mp="23v3q84pp5l2h" />
                  
                      <div className="brand-toggle w-12 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all shadow-inner" data-id="element-4639" data-mp="2cwtljlmp945j" />
                    </label>
                  </div>)}
              </div>

              <div className="px-8 py-5 bg-slate-50 border-t border-slate-100 flex items-center justify-between" data-id="element-4640" data-mp="1tzs9xhyn45sm">
                <p className="text-sm text-slate-500" data-id="element-4641" data-mp="xe8k9fqxuv6l">
                  <span className="font-semibold text-slate-700" data-id="element-4642" data-mp="11gv34r5mt5rn">
                    {activeNotifCount}
                  </span>{' '}
                  de {notifItems.length} notificaciones activas
                </p>
                <div className="flex items-center gap-3" data-id="element-4643" data-mp="p0lvvaafjamk">
                  {notifSaved && <div className="flex items-center gap-2 text-emerald-600 text-sm font-semibold" data-id="element-4644" data-mp="kwxpguxjal2w">
                      <CheckIcon className="w-4 h-4" data-id="element-4645" data-mp="fzfe6xw1ntrq" />
                      Guardado
                    </div>}
                  <Button onClick={handleSaveNotifs} leftIcon={<SaveIcon className="w-4 h-4" data-id="element-4647" data-mp="b1dy8ltx0akh" />} data-id="element-4646" data-mp="b0kvrx1zo785">
                  
                    Guardar Preferencias
                  </Button>
                </div>
              </div>
            </div>}

          {/* INTEGRACIONES TAB */}
          {activeTab === 'integraciones' && canSeeIntegraciones && <div className="space-y-6" data-id="element-4648" data-mp="3tbftmlkctgh8">
              {/* Google Maps Card */}
              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-id="element-4649" data-mp="3odwg5nvhd7qc">
                <div className="px-8 py-6 border-b border-slate-100" style={{
              background: 'linear-gradient(90deg, #e8f5e9, #f1f8e9)'
            }} data-id="element-4650" data-mp="3jg4o8tuu5lm5">
                
                  <div className="flex items-center gap-4" data-id="element-4651" data-mp="3erlfqekuqu5u">
                    <div className="w-12 h-12 rounded-xl bg-white shadow-sm flex items-center justify-center" data-id="element-4652" data-mp="2ztt2jt2s2hut">
                      <svg viewBox="0 0 24 24" className="w-7 h-7" fill="none" data-id="element-4653" data-mp="34s4b80hc6mbg">
                        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="#EA4335" data-id="element-4654" data-mp="3si8hkflwt065" />
                      
                        <circle cx="12" cy="9" r="3" fill="white" data-id="element-4655" data-mp="1am640sy7teq" />
                      </svg>
                    </div>
                    <div data-id="element-4656" data-mp="3is5d9j9c7zsi">
                      <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2 font-heading" data-id="element-4657" data-mp="3nri7ip2jg954">
                        Google Maps Platform
                        {googleKey && <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-bold rounded-full" data-id="element-4658" data-mp="fadqcja26616">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" data-id="element-4659" data-mp="24sanwon4bid9" />
                            Conectado
                          </span>}
                      </h3>
                      <p className="text-sm text-slate-500 mt-0.5" data-id="element-4660" data-mp="2eidob9wkjylm">
                        Mapas precisos, autocomplete de direcciones y GPS en
                        República Dominicana
                      </p>
                    </div>
                  </div>
                </div>

                <div className="p-8 space-y-6" data-id="element-4661" data-mp="1lvsu81opl8ed">
                  {/* Benefits */}
                  <div className="grid grid-cols-2 gap-3" data-id="element-4662" data-mp="1gwzjgh0t3vhw">
                    {[{
                  icon: '🗺️',
                  label: 'Mapas actualizados RD',
                  desc: 'Los más precisos disponibles'
                }, {
                  icon: '🔍',
                  label: 'Autocomplete',
                  desc: 'Búsqueda moderna de direcciones'
                }, {
                  icon: '📍',
                  label: 'GPS preciso',
                  desc: 'Ubicación exacta de farmacias'
                }, {
                  icon: '🧭',
                  label: 'Detección de llegada',
                  desc: 'Verificar visitas automáticamente'
                }].map(b => <div key={b.label} className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl" data-id="element-4663" data-mp="acdhyvasqcnd">
                    
                        <span className="text-lg" data-id="element-4664" data-mp="faf2zlrs8m1h">{b.icon}</span>
                        <div data-id="element-4665" data-mp="kbcsykj1akdj">
                          <p className="text-sm font-semibold text-slate-900" data-id="element-4666" data-mp="ka1o0hdur6ku">
                            {b.label}
                          </p>
                          <p className="text-xs text-slate-500" data-id="element-4667" data-mp="t57mswepgodv">{b.desc}</p>
                        </div>
                      </div>)}
                  </div>

                  {/* API Key Input */}
                  <div data-id="element-4668" data-mp="133f1t9pfowd8">
                    <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-4669" data-mp="133x7sy1weoyc">
                      API Key de Google Maps
                    </label>
                    <div className="flex gap-2" data-id="element-4670" data-mp="2sk0x5jop590b">
                      <div className="relative flex-1" data-id="element-4671" data-mp="2skso2h7cmik9">
                        <KeyIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-4672" data-mp="2nk4z012okbso" />
                        <input type="password" value={googleKeyInput} onChange={e => {
                      setGoogleKeyInput(e.target.value);
                      setKeyValidation('idle');
                    }} placeholder="AIzaSy..." className={`w-full pl-10 pr-4 py-3 text-sm border rounded-xl focus:outline-none focus:ring-2 transition-all bg-slate-50 focus:bg-white font-mono ${keyValidation === 'valid' ? 'border-emerald-400 ring-emerald-200' : keyValidation === 'invalid' ? 'border-rose-400 ring-rose-200' : 'border-slate-200'}`} data-id="element-4673" data-mp="2im37crr6v45l" />
                      
                      </div>
                      <Button variant="secondary" onClick={handleValidateGoogleKey} disabled={!googleKeyInput.trim() || isValidatingKey} data-id="element-4674" data-mp="3u1idom5af8dd">
                      
                        {isValidatingKey ? <Loader2Icon className="w-4 h-4 animate-spin" data-id="element-4675" data-mp="1nbokgq07sp9e" /> : 'Verificar'}
                      </Button>
                    </div>
                    {keyValidation === 'valid' && <p className="mt-2 text-sm text-emerald-600 font-medium flex items-center gap-1" data-id="element-4676" data-mp="1sd47gnlyd5uf">
                        <CheckIcon className="w-4 h-4" data-id="element-4677" data-mp="1ilhuomtyuypk" /> API Key válida —
                        Google Maps está listo
                      </p>}
                    {keyValidation === 'invalid' && <p className="mt-2 text-sm text-rose-600 font-medium flex items-center gap-1" data-id="element-4678" data-mp="14ntazibmxxm3">
                        <AlertTriangleIcon className="w-4 h-4" data-id="element-4679" data-mp="18l7in5jo3xbz" /> API Key
                        inválida — Verifica que esté correcta y tenga los APIs
                        habilitados
                      </p>}
                  </div>

                  {/* Instructions */}
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl" data-id="element-4680" data-mp="2p23e5y6w1zki">
                    <p className="text-sm font-semibold text-blue-800 mb-2" data-id="element-4681" data-mp="2tzlljk59c6qe">
                      ¿Cómo obtener tu API Key?
                    </p>
                    <ol className="text-xs text-blue-700 space-y-1.5 list-decimal list-inside" data-id="element-4682" data-mp="iaysuzhqfx59">
                      <li data-id="element-4683" data-mp="dcdocnvg6rb0">
                        Ve a{' '}
                        <a href="https://console.cloud.google.com/apis" target="_blank" rel="noopener noreferrer" className="underline font-semibold hover:text-blue-900" data-id="element-4684" data-mp="s4nwtnmly3gf">
                        
                          Google Cloud Console
                        </a>
                      </li>
                      <li data-id="element-4685" data-mp="w3wswz54edf5">Crea un proyecto nuevo o selecciona uno existente</li>
                      <li data-id="element-4686" data-mp="1b28k4q9q4tyx">
                        Habilita estos APIs:{' '}
                        <strong data-id="element-4687" data-mp="1635jdi0s19wr">Maps JavaScript API</strong>,{' '}
                        <strong data-id="element-4688" data-mp="160szv0fderhl">Places API</strong>,{' '}
                        <strong data-id="element-4689" data-mp="3cudavyfhll82">Geocoding API</strong>
                      </li>
                      <li data-id="element-4690" data-mp="37us9lhcuh0s6">
                        Ve a "Credenciales" → "Crear credenciales" → "API Key"
                      </li>
                      <li data-id="element-4691" data-mp="2m0a38wx2m909">Copia la API Key y pégala aquí</li>
                    </ol>
                  </div>
                </div>

                <div className="px-8 py-5 bg-slate-50 border-t border-slate-100 flex items-center justify-between" data-id="element-4692" data-mp="2qy3dq25epp6c">
                  <div data-id="element-4693" data-mp="30pzjlzdqijl4">
                    {googleKey && <button onClick={handleRemoveGoogleKey} className="text-sm text-rose-600 hover:text-rose-700 font-medium hover:underline" data-id="element-4694" data-mp="30oxyf2308gpo">
                    
                        Desconectar Google Maps
                      </button>}
                  </div>
                  <div className="flex items-center gap-3" data-id="element-4695" data-mp="2d0n1ga0fj16o">
                    {integrationSaved && <div className="flex items-center gap-2 text-emerald-600 text-sm font-semibold" data-id="element-4696" data-mp="2d2rebhgb3aj3">
                        <CheckIcon className="w-4 h-4" data-id="element-4697" data-mp="3ofk7mwoqw719" />
                        Guardado
                      </div>}
                    <Button onClick={handleSaveGoogleKey} disabled={!googleKeyInput.trim()} leftIcon={<SaveIcon className="w-4 h-4" data-id="element-4699" data-mp="1gtsg5uut25jr" />} data-id="element-4698" data-mp="3sgm81gueep4t">
                    
                      Guardar API Key
                    </Button>
                  </div>
                </div>
              </div>

              {/* Status info */}
              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6" data-id="element-4700" data-mp="180gitapmdn4f">
                <h4 className="text-sm font-bold text-slate-900 mb-3 font-heading" data-id="element-4701" data-mp="1336ujydyvhi6">
                  Estado de Integraciones
                </h4>
                <div className="space-y-3" data-id="element-4702" data-mp="y17ryqq1woma">
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl" data-id="element-4703" data-mp="t3y4vhq4wp1m">
                    <div className="flex items-center gap-3" data-id="element-4704" data-mp="p1ufdp6mrjxc">
                      <div className="w-8 h-8 rounded-lg bg-white shadow-sm flex items-center justify-center" data-id="element-4705" data-mp="p4gx2hn8jfta">
                        <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" data-id="element-4706" data-mp="2jgn73d4e7qk0">
                        
                          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="#EA4335" data-id="element-4707" data-mp="2ehtx3uobehol" />
                        
                          <circle cx="12" cy="9" r="2.5" fill="white" data-id="element-4708" data-mp="1smexwrvkog7y" />
                        </svg>
                      </div>
                      <div data-id="element-4709" data-mp="1no3ogi96w7q9">
                        <p className="text-sm font-medium text-slate-900" data-id="element-4710" data-mp="1im4l38dg9atb">
                          Google Maps
                        </p>
                        <p className="text-xs text-slate-500" data-id="element-4711" data-mp="1emngsi9rejpq">
                          Mapas, Places, Geocoding
                        </p>
                      </div>
                    </div>
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-bold rounded-full ${googleKey ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-slate-100 text-slate-500 border border-slate-200'}`} data-id="element-4712" data-mp="26cs7szs0y4zx">
                    
                      <span className={`w-1.5 h-1.5 rounded-full ${googleKey ? 'bg-emerald-500' : 'bg-slate-400'}`} data-id="element-4713" data-mp="2u22zlrrecp1x" />
                    
                      {googleKey ? 'Activo' : 'No configurado'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl" data-id="element-4714" data-mp="bujk8ky4qw4k">
                    <div className="flex items-center gap-3" data-id="element-4715" data-mp="gtnr34xl0hrq">
                      <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center" data-id="element-4716" data-mp="ks3jjocfmicj">
                        <MapPinIcon className="w-4 h-4 text-blue-600" data-id="element-4717" data-mp="pt12w496hifm" />
                      </div>
                      <div data-id="element-4718" data-mp="uqjczxyxm3p7">
                        <p className="text-sm font-medium text-slate-900" data-id="element-4719" data-mp="up8b63p1r60q">
                          Photon + OSM
                        </p>
                        <p className="text-xs text-slate-500" data-id="element-4720" data-mp="14gm5zy61zhnj">
                          Búsqueda gratuita (fallback)
                        </p>
                      </div>
                    </div>
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-bold rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200" data-id="element-4721" data-mp="1dic5lra39mwe">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" data-id="element-4722" data-mp="1iekeimxp0jv4" />
                      Siempre activo
                    </span>
                  </div>
                </div>
              </div>
            </div>}
        </div>
      </div>
    </div>;
}